import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { kycApi } from '@/api/kyc';
import { Container, Flex } from '@/ui/layout';
import { Body1Light } from '@/ui/typography';
import { displayFullDate, parseType } from '@/utils/helpers/common';
import { useTranslation } from 'react-i18next';
import { Link } from '@/ui';
import { Pagination, Typography } from 'antd';
import { useLoadingState } from '@/hooks';
import styled from 'styled-components';

const ITEMS_ON_PAGE = 10;

export const InvestorActivity = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const [page, setPage] = useState(1);
  const { data, refetch, isFetching } = useQuery({
    queryKey: ['getInvestorActivity'],
    queryFn: () =>
      kycApi.getInvestorActivity({
        investorId: id,
        reverse: true,
        take: ITEMS_ON_PAGE,
        skip: (page - 1) * ITEMS_ON_PAGE
      })
  });

  useLoadingState(isFetching, 'Fetching');

  useEffect(() => {
    refetch();
  }, [page]);

  const logs = useMemo(() => {
    if (data && data.data) {
      return { logs: data.data.logs, count: data.data.count };
    }
    return null;
  }, [data]);

  return (
    <Container $paddingY={25} $marginBottom={40}>
      <Flex align={'center'} justify={'space-between'} wrap>
        <Typography.Title level={2}>{t('Activity log')}</Typography.Title>
      </Flex>

      <Flex vertical $marginTop={14}>
        {logs?.logs?.map((log, key) => {
          const logText = parseType(log.type);

          return (
            <Log key={key}>
              {log.createdAt && (
                <Body1Light $marginBottom={12}>
                  <strong>{displayFullDate(new Date(log.createdAt))}</strong>
                </Body1Light>
              )}

              {log.type && (
                <Container $marginBottom={4}>
                  {log.investorProductId ? (
                    <Link to={`/investor/${id}/product/${log.investorProductId}`}>
                      {logText.title}
                    </Link>
                  ) : log.investorQuestionSectionTypeId ? (
                    <Link to={`/investor/${id}/questionnaire/${log.investorQuestionSectionTypeId}`}>
                      {logText.title}
                    </Link>
                  ) : log.requestDocumentId ? (
                    <Link to={`/investor/${id}/documents/${log.requestDocumentId}`}>
                      {logText.title}
                    </Link>
                  ) : !log.investorProductId &&
                    !log.investorQuestionSectionTypeId &&
                    logText.title.includes('User identification') ? (
                    <Link to={`/investor/${id}/sumsub-verification`}>{logText.title}</Link>
                  ) : (
                    <span>{logText.title}</span>
                  )}{' '}
                  <span>{logText.restText}</span>
                </Container>
              )}
              {log.text && <Container>{log.text}</Container>}
            </Log>
          );
        })}

        {logs?.count && (
          <Pagination
            hideOnSinglePage={true}
            current={page}
            total={logs.count}
            pageSize={ITEMS_ON_PAGE}
            onChange={setPage}
            align='center'
            style={{ margin: '20px 0' }}
          />
        )}
      </Flex>
    </Container>
  );
};

const Log = styled.div`
  padding: 16px 0;
  border-bottom: 1px solid #dde4e9;

  &:last-child {
    border-bottom: none;
  }
`;
